import React, { useEffect, useState } from 'react';
import { PlusIcon, Trash2Icon } from 'lucide-react';
import { useAppState } from '../../contexts/AppStateContext';
import { FamilyRules, Reward } from '../../types/family';
import { Effective } from '../../types/shared';
import { uid } from '../../utils/db';
import { Button } from '../ui/Button';
import { Chip, EffectiveChoice, Field, Toggle } from '../ui/Form';
import { namesOf } from '../../utils/members';
import { opportunityLabels, opportunityOn, rewardEnabledFor, toggleRewardFor } from '../../utils/rewards';
import { OpportunityKey } from '../../types/family';
import { Sheet } from '../ui/Sheet';
type NumKey = 'basicThresholdPct' | 'allowanceAmount' | 'houseCategoryValue' | 'roomBonus' | 'roomBonusWeeklyMax' | 'extraMinutesStep' | 'extraMinutesBonus' | 'extraDailyMax' | 'lessonDayBonus';
const fields: {
  key: NumKey;
  label: string;
  suffix: string;
}[] = [{
  key: 'basicThresholdPct',
  label: 'Kačing – prag cvenkotočk',
  suffix: '%'
}, {
  key: 'allowanceAmount',
  label: 'Žepnina na otroka',
  suffix: '€ / teden'
}, {
  key: 'houseCategoryValue',
  label: 'Privzeta vrednost hišne kategorije',
  suffix: 'cvenkotočk'
}, {
  key: 'roomBonus',
  label: 'Urejena soba (dan poleg sobote)',
  suffix: 'goodies'
}, {
  key: 'roomBonusWeeklyMax',
  label: 'Soba – največ na teden',
  suffix: '×'
}, {
  key: 'extraMinutesStep',
  label: 'Dodatne minute – korak',
  suffix: 'min'
}, {
  key: 'extraMinutesBonus',
  label: 'Goodies na korak',
  suffix: 'goodies'
}, {
  key: 'extraDailyMax',
  label: 'Dodatne minute – največ na dan',
  suffix: 'točk'
}, {
  key: 'lessonDayBonus',
  label: 'Prostovoljna vadba na dan pouka',
  suffix: 'goodies'
}];
export function PointsSettings() {
  const {
    family,
    updateRules,
    updateRulesNow,
    saveReward,
    deleteReward
  } = useAppState();
  const r = family.rules;
  const pick = (rules: FamilyRules) => Object.fromEntries(fields.map(f => [f.key, rules[f.key]])) as Record<NumKey, number>;
  const [vals, setVals] = useState(pick(r));
  const [day, setDay] = useState(r.payoutDay);
  const [time, setTime] = useState(r.payoutTime);
  const [effective, setEffective] = useState<Effective>('naslednji-teden');
  const [reward, setReward] = useState<Reward | null>(null);
  useEffect(() => {
    setVals(pick(r));
    setDay(r.payoutDay);
    setTime(r.payoutTime);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [r]);
  const changed = fields.filter(f => vals[f.key] !== r[f.key]);
  return <div className="space-y-6" data-id="element-697" data-mp="3uqr00jr131v1">
      <section data-id="element-698" data-mp="3urs7yfcqz88j">
        <h3 className="font-display text-lg font-semibold" data-id="element-699" data-mp="3urafzux4bg6f">Točkovnik in žepnina</h3>
        <div className="mt-3 grid gap-3 sm:grid-cols-2" data-id="element-700" data-mp="3pyz5xz8nye05">
          {fields.map(f => <Field key={f.key} label={f.label} data-id="element-701" data-mp="3lz8eruf85vv3">
              {id => <div className="flex items-center gap-2" data-id="element-702" data-mp="map3w596w6z3">
                  <input id={id} type="number" min={0} className="field" value={vals[f.key]} onChange={e => setVals(v => ({
              ...v,
              [f.key]: Math.max(0, Number(e.target.value))
            }))} data-id="element-703" data-mp="mc8kxy2o6m1x" />
                  <span className="shrink-0 text-sm text-muted" data-id="element-704" data-mp="208jlnpixqaow">{f.suffix}</span>
                </div>}
            </Field>)}
        </div>
        {changed.length > 0 && <div className="mt-3 space-y-3" data-id="element-705" data-mp="296vii9aea4ps">
            <EffectiveChoice value={effective} onChange={setEffective} data-id="element-706" data-mp="2e6qegr911khp" />
            <Button onClick={() => updateRules(vals, 'Točkovnik', changed.map(f => `${f.label}: ${r[f.key]} → ${vals[f.key]}`).join('; '), effective)} data-id="element-707" data-mp="2e81d6cnm821y">Shrani točkovnik</Button>
          </div>}
        <div className="mt-4 grid gap-3 sm:grid-cols-[1fr_1fr_auto] sm:items-end" data-id="element-708" data-mp="2o2td3n4o70zw">
          <Field label="Dan izplačila" data-id="element-709" data-mp="2sw60jtxm51r2">
            {id => <select id={id} className="field" value={day} onChange={e => setDay(e.target.value)} data-id="element-710" data-mp="2wxhhlb2f8enn">
                {['ponedeljek', 'torek', 'sreda', 'četrtek', 'petek', 'sobota', 'nedelja'].map(d => <option key={d} data-id="element-711" data-mp="36wgtr3measfe">{d}</option>)}
              </select>}
          </Field>
          <Field label="Ura" data-id="element-712" data-mp="2t73ynkns71bl">{id => <input id={id} className="field" value={time} onChange={e => setTime(e.target.value)} data-id="element-713" data-mp="2t6lwk7eftsvr" />}</Field>
          <Button variant="secondary" disabled={day === r.payoutDay && time === r.payoutTime} onClick={() => updateRulesNow({
          payoutDay: day,
          payoutTime: time
        }, 'Žepnina', `Izplačilo: ${day} ${time}`)} data-id="element-714" data-mp="2o7r9uudcnk6d">
            Shrani
          </Button>
        </div>
      </section>

      <section data-id="element-715" data-mp="2aa2rpuao2r50">
        <h3 className="font-display text-lg font-semibold" data-id="element-716" data-mp="2abx4umfp5eyj">Goodies priložnosti</h3>
        <p className="text-xs text-muted" data-id="element-717" data-mp="25ckgtyd3a1ud">Kaj otroci vidijo na zaslonu Danes kot priložnost za goodies.</p>
        <div className="mt-2 divide-y divide-line rounded-3xl border-[1.5px] border-line bg-surface px-4" data-id="element-718" data-mp="2k5xen1lxb8dl">
          {(Object.keys(opportunityLabels) as OpportunityKey[]).map(k => <Toggle key={k} checked={opportunityOn(r, k)} onChange={v => updateRulesNow({
          opportunities: {
            ...r.opportunities,
            [k]: v
          }
        }, 'Goodies priložnosti', `${opportunityLabels[k].label}: ${v ? 'vklopljeno' : 'izklopljeno'}`)} label={opportunityLabels[k].label} hint={opportunityLabels[k].hint} data-id="element-719" data-mp="3vlm542ydypj5" />)}
        </div>
      </section>

      <section data-id="element-720" data-mp="3p99r1tgb4chw">
        <div className="flex items-center justify-between" data-id="element-721" data-mp="3t88qwzn6lj2h">
          <h3 className="font-display text-lg font-semibold" data-id="element-722" data-mp="3t8saslwe6hj5">Nagrade</h3>
          <Button size="sm" onClick={() => setReward({
          id: uid('r'),
          title: '',
          cost: 30
        })} data-id="element-723" data-mp="ku05qcmznukc">
            <PlusIcon className="h-4 w-4" aria-hidden="true" data-id="element-724" data-mp="pqqleyuvso58" /> Nagrada
          </Button>
        </div>
        <ul className="mt-2 divide-y divide-line rounded-3xl border-[1.5px] border-line bg-surface" data-id="element-725" data-mp="bzlt6htuey4x">
          {family.rewards.length === 0 && <li className="px-4 py-3 text-sm text-muted" data-id="element-726" data-mp="fzvw1lmc98fk">Katalog je prazen.</li>}
          {family.rewards.map(rw => <li key={rw.id} className="flex items-center gap-3 px-4 py-3" data-id="element-727" data-mp="18i9n92d65w5o">
              <button type="button" onClick={() => setReward(rw)} className="min-w-0 flex-1 text-left" data-id="element-728" data-mp="18ke2gggiqhl9">
                <span className="block font-bold hover:underline" data-id="element-729" data-mp="1ibxehf1pz81g">{rw.title}</span>
                <span className="block text-xs text-muted" data-id="element-730" data-mp="1edg48mn05hft">
                  {rw.paused ? 'Na pavzi · ' : ''}
                  {rw.availableFor ? rw.availableFor.length ? `Za: ${namesOf(family, rw.availableFor)}` : 'Nihče' : 'Vsi otroci'}
                </span>
              </button>
              <span className="rounded-full bg-bonus-soft px-2 py-0.5 text-xs font-extrabold text-bonus-ink" data-id="element-731" data-mp="zgyvm3676gwy">{rw.cost} goodies</span>
              <button type="button" onClick={() => deleteReward(rw.id)} aria-label={`Odstrani ${rw.title}`} className="grid h-9 w-9 place-items-center rounded-xl text-muted hover:bg-canvas" data-id="element-732" data-mp="umulxc5jrus6">
                <Trash2Icon className="h-4 w-4" data-id="element-733" data-mp="lm68aik2fwft" />
              </button>
            </li>)}
        </ul>
        <p className="mt-2 text-xs text-muted" data-id="element-734" data-mp="bptfro09bomn">Pogoj koriščenja: kačing v zadnjem zaključenem tednu, z izjemo po presoji starša. Točke se odštejejo ob odobritvi.</p>
      </section>
      <RewardSheet reward={reward} onClose={() => setReward(null)} onSave={saveReward} data-id="element-735" data-mp="1nz6os1ugj00" />
    </div>;
}
function RewardSheet({
  reward,
  onClose,
  onSave
}: {
  reward: Reward | null;
  onClose: () => void;
  onSave: (r: Reward, e: Effective) => void;
}) {
  const {
    family,
    activeMembers
  } = useAppState();
  const kidIds = activeMembers.filter(m => m.role === 'otrok').map(m => m.id);
  const [rw, setRw] = useState<Reward | null>(reward);
  const [effective, setEffective] = useState<Effective>('naslednji-teden');
  useEffect(() => setRw(reward), [reward]);
  if (!rw) return <Sheet open={false} onClose={onClose} title="" children={null} data-id="element-736" data-mp="3oy71iu4sofpe" />;
  const prev = family.rewards.find(x => x.id === rw.id);
  return <Sheet open onClose={onClose} title={prev ? 'Uredi nagrado' : 'Nova nagrada'} footer={<Button className="w-full" disabled={!rw.title.trim() || rw.cost <= 0} onClick={() => {
    onSave({
      ...rw,
      title: rw.title.trim()
    }, effective);
    onClose();
  }} data-id="element-738" data-mp="3194ltrtq0wxj">
          Shrani
        </Button>} data-id="element-737" data-mp="3tri6epq1qmeo">
      <div className="space-y-4" data-id="element-739" data-mp="f9ddwou8ezjx">
        <Field label="Naziv" data-id="element-740" data-mp="fag22p066nw5">{id => <input id={id} className="field" value={rw.title} onChange={e => setRw({
          ...rw,
          title: e.target.value
        })} data-id="element-741" data-mp="fb7y20xoinmy" />}</Field>
        <Field label="Cena (goodies)" data-id="element-742" data-mp="o90ej8f501bx">{id => <input id={id} type="number" min={1} className="field" value={rw.cost} onChange={e => setRw({
          ...rw,
          cost: Number(e.target.value)
        })} data-id="element-743" data-mp="o7ypu1577gbq" />}</Field>
        <div data-id="element-744" data-mp="y7zfe4tnusod">
          <p className="mb-1.5 text-sm font-bold" data-id="element-745" data-mp="y8ittbk3qyxn">Na voljo za</p>
          <div className="flex flex-wrap gap-2" data-id="element-746" data-mp="1bz4glbcoft0t">
            {activeMembers.filter(m => m.role === 'otrok').map(m => <Chip key={m.id} color={m.color} active={rewardEnabledFor(rw, m.id)} onClick={() => setRw({
            ...rw,
            availableFor: toggleRewardFor(rw, m.id, kidIds)
          })} data-id="element-747" data-mp="171cg3sxyuyl0">
                  {m.name}
                </Chip>)}
          </div>
        </div>
        <div className="rounded-2xl border-[1.5px] border-line px-3" data-id="element-748" data-mp="20jh9nzsst7ns">
          <Toggle checked={!rw.paused} onChange={v => setRw({
          ...rw,
          paused: !v
        })} label="Aktivna" hint="Nagrada na pavzi je otrokom skrita." data-id="element-749" data-mp="20i6723b3a5y9" />
        </div>
        {prev && prev.cost !== rw.cost && <EffectiveChoice value={effective} onChange={setEffective} data-id="element-750" data-mp="1qnxfbhtvuh15" />}
      </div>
    </Sheet>;
}